import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
} from "@mui/material";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getNoteTypeColor(noteType) {
  switch (noteType?.toLowerCase()) {
    case "progress note":
      return "info";
    case "assessment":
      return "warning";
    case "plan":
      return "success";
    case "other":
      return "default";
    default:
      return "default";
  }
}

function NotesCard({ notes, onAddNote, onDeleteNote, onEditNote }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [expandedNoteId, setExpandedNoteId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveNoteId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveNoteId(null);
  };

  const toggleExpand = (noteId) => {
    setExpandedNoteId(expandedNoteId === noteId ? null : noteId);
  };

  const handleEdit = (note) => {
    handleMenuClose();
    if (onEditNote) {
      onEditNote(note);
    }
  };

  const handleDelete = (noteId) => {
    handleMenuClose();
    if (onDeleteNote) {
      onDeleteNote(noteId);
    }
  };

  return (
    <GlassCard
      title="Clinical Notes"
      addLabel="Add Note"
      icon={<DocumentScannerIcon color="info" />}
      onAdd={onAddNote}
      data-testid="notes-card"
      data-card-category="clinical_notes"
    >
      {notes?.length ? (
        notes.map((note) => (
          <Box
            key={note.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`note-item-${note.id}`}
            data-note-type={note.note_type}
            data-note-author={note.author}
            data-note-date={note.created_at}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500} mb={0.25}>
                  {note.author || "Unknown"} • {note.created_at ? note.created_at.split("T")[0] : "N/A"}
                </Typography>
              </Box>

              <Box display="flex" gap={0.5} alignItems="center">
                <Chip
                  size="small"
                  label={note.note_type || "Note"}
                  color={getNoteTypeColor(note.note_type)}
                  variant="outlined"
                  data-testid={`note-type-chip-${note.id}`}
                  data-field-name="note_type"
                />
                <Tooltip title={expandedNoteId === note.id ? "Collapse" : "Expand"}>
                  <IconButton
                    size="small"
                    onClick={() => toggleExpand(note.id)}
                    data-testid={`note-expand-${note.id}`}
                    data-action="expand"
                    aria-label={expandedNoteId === note.id ? "Collapse note" : "Expand note"}
                  >
                    {expandedNoteId === note.id ? (
                      <ExpandLessIcon aria-label={expandedNoteId === note.id ? "Collapse note" : "Expand note"} fontSize="small" />
                    ) : (
                      <ExpandMoreIcon aria-label={expandedNoteId === note.id ? "Collapse note" : "Expand note"} fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Options">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, note.id)}
                    data-testid={`note-menu-${note.id}`}
                    aria-label="Note options menu"
                    data-action="note-menu"
                    data-note-id={note.id}
                  >
                    <MoreVertIcon aria-label="Note options menu" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Expanded content */}
            <Collapse in={expandedNoteId === note.id} timeout="auto">
              <Box
                mt={1}
                pt={1}
                sx={{ borderTop: "1px solid", borderColor: "divider" }}
                data-testid={`note-details-${note.id}`}
                data-action="view_details"
              >
                <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-wrap" }}>
                  {note.content}
                </Typography>
                {note.author && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Author: {note.author}
                  </Typography>
                )}
                {note.created_at && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created: {new Date(note.created_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No notes recorded
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {onEditNote && (
          <MenuItem
            onClick={() => {
              const note = notes?.find((n) => n.id === activeNoteId);
              if (note) {
                handleEdit(note);
              }
            }}
            data-testid={`note-menu-edit-${activeNoteId}`}
            data-action="edit"
            aria-label="Edit note"
            data-note-id={activeNoteId}
          >
            Edit
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleDelete(activeNoteId)}
          data-testid={`note-menu-delete-${activeNoteId}`}
          data-action="delete"
          aria-label="Delete note"
          data-note-id={activeNoteId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1}} />
          Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

NotesCard.propTypes = {
  notes: PropTypes.array,
  onAddNote: PropTypes.func.isRequired,
  onDeleteNote: PropTypes.func.isRequired,
  onEditNote: PropTypes.func,
};

NotesCard.defaultProps = {
  notes: [],
  onEditNote: null,
};

export default NotesCard;
